import { QueryFunctionContext } from '@tanstack/react-query';

import { axiosHelper } from '@setvi/shared/services';
import { AxiosMethods, QuotesRFQApi } from '@setvi/shared/enums';
import { prepareReqBody } from 'pages/sales/modules/orders/utils/prepare-body-req';

import { QuoteFilters, QuotesStatus, QuoteSummaryStatsBody } from './Types';

export enum QuotesQueryKey {
  GET_ALL = 'GET_ALL_QUOTES',
  GET_SINGLE = 'GET_SINGLE_QUOTE',
  GET_LIST_ANALYTICS = 'GET_LIST_ANALYTICS_QUOTES',
  PREVIEW_PDF = 'PREVIEW_QUOTE_PDF',
  GET_QUOTE_CONFIGURATIONS = 'GET_QUOTE_CONFIGURATIONS',
  GET_QUOTE_STATUSES = 'GET_QUOTE_STATUSES',
  SUMMARY_STATS = 'GET_QUOTES_SUMMARY_STATS'
}

/**
 * Query for fetching all quotes with infinite scroll pagination
 * Following Auto-RFQ pattern exactly
 */
export const getQuotesQuery = (params: QuoteFilters) => ({
  queryKey: [QuotesQueryKey.GET_ALL, params],
  queryFn: async (data: QueryFunctionContext) => {
    const offset = data.pageParam || 0;
    const limit = params.limit || 20;

    // Prepare request body using existing utility
    const body = prepareReqBody(
      {
        statusId: params.statusId,
        date: params.date,
        searchStr: params.searchStr,
        accountName: params.accountName,
        accountId: params?.accountId,
        type: params.type
      },
      offset,
      limit,
      true,
      null
    );

    const response = await axiosHelper({
      endpoint: '/quote/search',
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body
    });

    return response.data;
  },
  getNextPageParam: (lastPage: any, allPages: any) => {
    const limit = params.limit || 20;
    const allQuotes = allPages.reduce(
      (acc: any, curr: any) => acc + (curr?.length || 0),
      0
    );

    // If last page has less items than limit, we've reached the end
    return lastPage?.length < limit ? undefined : allQuotes;
  },
  cacheTime: 0, // Don't cache to ensure fresh data
  refetchOnWindowFocus: false
});

/**
 * Query for fetching a single quote by ID
 */
export const getQuoteQuery = (id: string | number) => ({
  queryKey: [QuotesQueryKey.GET_SINGLE, id],
  queryFn: () =>
    axiosHelper({
      endpoint: `/quote/${id}`,
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    })
});

/**
 * Query for fetching quote PDF preview
 */
export const getQuotePreviewQuery = (
  quoteId: string | number,
  showManufacturerDetails?: boolean
) => ({
  queryKey: [QuotesQueryKey.PREVIEW_PDF, { quoteId, showManufacturerDetails }],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesRFQApi.PreviewPDF.replace(
        ':quoteId',
        quoteId?.toString()
      ),
      params: { showManufacturerDetails },
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    })
});

export const getQuoteConfigurationsQuery = () => ({
  queryKey: [QuotesQueryKey.GET_QUOTE_CONFIGURATIONS],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesRFQApi.Configurations,
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    })
});

export const getQuoteStatusesQuery = () => ({
  queryKey: [QuotesQueryKey.GET_QUOTE_STATUSES],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesRFQApi.GetStatuses,
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    }),
  select: (res: QuotesStatus[]) => res.sort((a, b) => a.order - b.order)
});

export const getQuoteSummaryStatsQuery = (body: QuoteSummaryStatsBody) => ({
  queryKey: [QuotesQueryKey.SUMMARY_STATS, body],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesRFQApi.Summary,
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body
    })
});
